import { Link, useParams } from "react-router-dom";
import { Button, Header, Icon, Segment } from "semantic-ui-react";

import AppSettings from "../../../../../../config";

interface NotifyContentListCardProps {
  data: {
    id: number;
    authorId?: number;
    body: string;
    cityId: number;
    createdAt: string;
    deactivatedAt?: string | null;
    expiredAt?: string | null;
    headline: string;
    isDraft?: boolean;
    publishedAt: string | null;
    lastNotifiedAt?: string | null;
    scheduledAt?: string | null;
    subscriptionGroups: {
      autoSubscribe: boolean;
      cityId: number;
      id: number;
      name: string;
    }[];
    tagline?: string | null;
    updatedAt: string;
    voiceAudioResourceId?: number | null;
    voiceBody?: string | null;
    voiceForceSendAll?: boolean | null;
  };
  isMobileView?: boolean;
}

export const NotifyContentListCard = (props: NotifyContentListCardProps) => {
  const publishedAtDate = new Date(props.data.publishedAt ?? new Date().toISOString());

  const params = useParams<{ site: string }>();

  // Find the first image URL in the markdown
  const imageRegex = /!\[.*?\]\((.*?)\)/;
  const match = props.data.body.match(imageRegex);
  const firstImageUrl = match ? match[1] : null;

  // Join subscription group names
  const joinedSubscriptionGroups = props.data.subscriptionGroups.map((group) => group.name).join("  •  ");
  const dateAndSubscriptionGroups = `${formattedDate(publishedAtDate)}${
    props.data.subscriptionGroups.length > 0 ? "  •  " + joinedSubscriptionGroups : ""
  }`;

  const strippedMarkdown = stripMarkdown(props.data.body);

  return (
    <Link to={`posts/${props.data.id}`}>
      <Segment className='tw-h-[190px] tw-cursor-pointer tw-p-[16px] '>
        {props.isMobileView ? (
          // Mobile Layout - Full height flex container
          <div className='tw-flex tw-h-full tw-flex-col'>
            {/* Top section with content */}
            <div className={`tw-flex tw-min-h-0 tw-flex-1 tw-flex-row `}>
              <div
                className={
                  firstImageUrl
                    ? "tw-flex tw-min-h-0 tw-flex-1 tw-basis-[70%] tw-flex-col "
                    : "tw-flex tw-min-h-0 tw-flex-1 tw-basis-full tw-flex-col "
                }
              >
                <Header as='h3' className='tw-mb-0 tw-line-clamp-2 tw-flex-shrink-0  '>
                  {props.data.headline}
                </Header>
                <div className='tw-mt-4 tw-flex tw-min-h-0 tw-flex-1 tw-items-start  '>
                  <p className='tw-line-clamp-1 tw-overflow-hidden tw-text-ellipsis tw-whitespace-normal tw-font-medium tw-text-[#4D4D4D]'>
                    {props.data.tagline ?? strippedMarkdown}
                  </p>
                </div>
              </div>
              {firstImageUrl && (
                <div className='tw-ml-3 tw-flex tw-aspect-square tw-max-h-[100px] tw-max-w-[100px]  tw-flex-none tw-basis-[30%] tw-items-center tw-justify-center  '>
                  <img
                    src={firstImageUrl}
                    alt={`headline ${props.data.headline}`}
                    className='tw-h-full tw-w-full tw-rounded-sm tw-object-cover'
                  />
                </div>
              )}
            </div>
            <div className='tw-mt-2 tw-flex tw-h-6 tw-w-full tw-flex-shrink-0 tw-flex-row tw-items-center tw-text-[#757575]  '>
              <div
                className={
                  firstImageUrl
                    ? "tw-flex tw-flex-1 tw-basis-[70%] tw-flex-col tw-overflow-hidden  "
                    : "tw-flex tw-flex-1 tw-overflow-hidden "
                }
              >
                <p className='tw-mb-0 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-[12px]'>
                  {dateAndSubscriptionGroups}
                </p>
              </div>
              <div
                className={
                  firstImageUrl
                    ? "tw-ml-1 tw-flex tw-flex-1 tw-justify-end  "
                    : "tw-ml-2 tw-flex tw-flex-shrink-0 tw-justify-end "
                }
              >
                <div className='tw-ml-1 tw-flex tw-w-[112px] tw-justify-end'>
                  <button
                    className='ui button button-ghost black center-icon tw-flex-shrink-0  tw-p-0   '
                    onClick={async (e) => {
                      try {
                        e.preventDefault();
                        await navigator.share({
                          title: props.data.headline,
                          text: props.data.tagline || undefined,
                          url: `${AppSettings.GOGOV_BASE_API_URL}/core/notify/public/share/${params.site}/${props.data.id}`,
                        });
                      } catch (error) {
                        console.error("Error sharing:");
                      }
                    }}
                  >
                    <Icon name='share' className='tw-color-black tw-m-0 tw-opacity-70' />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className='tw-flex tw-h-full tw-flex-row   tw-p-[0px] '>
            <div
              className={
                firstImageUrl
                  ? "tw-mr-2 tw-flex tw-flex-1 tw-basis-[70%] tw-flex-col"
                  : "tw-flex tw-flex-1 tw-basis-full tw-flex-col"
              }
            >
              {/* Headline */}
              <Header as='h3' className='tw-mb-[14px] tw-line-clamp-2 '>
                {props.data.headline}
              </Header>

              {/* Tagline/Body (Middle Div) */}
              <div className='tw-mb-[14px] tw-flex-grow'>
                <p className='tw-line-clamp-2 tw-overflow-hidden tw-text-ellipsis tw-whitespace-normal tw-font-medium tw-text-[#4D4D4D]'>
                  {props.data.tagline ?? strippedMarkdown}
                </p>
              </div>

              {/* Date and Subscription Groups */}
              <div className='tw-flex tw-items-center tw-text-[#757575] '>
                <p className='tw-mb-0 tw-line-clamp-1 tw-overflow-hidden tw-text-ellipsis tw-text-[12px]'>
                  {dateAndSubscriptionGroups}
                </p>
                <Button
                  className='button-ghost tw-color-black tw-ml-auto tw-p-2'
                  icon
                  onClick={async (e) => {
                    try {
                      e.preventDefault();
                      await navigator.share({
                        title: props.data.headline,
                        text: props.data.tagline || undefined,
                        url: `${AppSettings.GOGOV_BASE_API_URL}/core/notify/public/share/${params.site}/${props.data.id}`,
                      });
                    } catch (error) {
                      console.error("Error sharing:");
                    }
                  }}
                >
                  <Icon name='share' style={{ color: "black", opacity: 0.7 }} />
                </Button>
              </div>
            </div>
            {firstImageUrl && (
              <div className='tw-ml-1 tw-flex tw-aspect-square tw-flex-none  tw-items-center tw-justify-center'>
                <img
                  src={firstImageUrl}
                  alt={`headline ${props.data.headline}`}
                  className='tw-h-full tw-w-full tw-rounded-sm tw-object-cover'
                />
              </div>
            )}
          </div>
        )}
      </Segment>
    </Link>
  );
};

// function that formats Date object into "MMM, DD,YYYY" format
function formattedDate(date: Date) {
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
}
// function that removes all markdown from a string
function stripMarkdown(markdownText: string): string {
  return markdownText
    .replace(/!\[.*?\]\(.*?\)/g, "") // images
    .replace(/\[(.*?)\]\(.*?\)/g, "$1") // Extract link text and remove URL
    .replace(/[#]+ .*?/g, "") // headers
    .replace(/(\*\*|__)(.*?)\1/g, "$2") // bold text
    .replace(/(\*|_)(.*?)\1/g, "$2") // italic text
    .replace(/~~(.*?)~~/g, "$1") // strikethrough text
    .replace(/`{1,2}.*?`{1,2}/g, "") // inline code
    .replace(/```[\s\S]*?```/g, "") // code blocks
    .replace(/>\s?.*/g, "") // blockquotes
    .replace(/[-+*]\s?.*/g, "") // unordered list items
    .replace(/\d+\.\s?.*/g, "") // ordered list items
    .replace(/\\n{2,}/g, " ") // newlines
    .replace(/\\n/g, " ")
    .trim();
}
